import type { Locale } from '@/i18n/locale.ts'

/**
 * The decisions behind keeping a tab's language and an account's language in step
 * (PRD-27 D-35), as pure functions — so every branch can be proved without a browser, a
 * session or a clock.
 *
 * ── The three signals, and what each one IS ──
 *
 *   a CHOICE      somebody pressed the switcher. The only signal that is a decision, and the
 *                 only one that is ever written to an account.
 *   the ACCOUNT   what `users.locale` says — which is a decision only if `localeChosen`, and
 *                 otherwise just the default the account was created with.
 *   an INFERENCE  what the served document, the browser or the brand suggested. Good enough
 *                 to paint with; never good enough to record as somebody's preference.
 *
 * Every rule below follows from keeping those apart. The first cut of this sync did not: it
 * compared the tab to the account and acted on any difference, and a difference cannot say
 * WHO moved. Two effects each concluded the other side was stale, and the language flipped
 * ~70 times a second until whichever request landed last happened to win.
 */

/** Where an explicit choice waits until an account has confirmed it. */
export const UNSENT_CHOICE_KEY = 'ralph.locale.unsent'

/** The owner of a choice made before anybody was signed in — on the sign-in page. */
export const ANONYMOUS = 'anon'

/** An explicit choice no account has confirmed yet, and who was signed in when it was made. */
export type UnsentChoice = { owner: string; locale: Locale; seq: number }

/**
 * Should the account's language be applied to this tab?
 *
 * Only when it is a DECISION, only when the person has not made a newer one here that is
 * still on its way up, and only when it would change anything.
 *
 * The first condition is the one that protects people. Every account that existed before this
 * shipped holds the literal 'en' it was created with, which nobody chose; honouring it would
 * flip every Portuguese reader on an existing deployment to English at sign-in.
 */
export function shouldAdoptAccountLocale(input: {
  accountLocale: Locale
  accountChose: boolean
  tabLocale: Locale
  hasUnsentChoice: boolean
}): boolean {
  if (!input.accountChose) return false
  if (input.hasUnsentChoice) return false
  return input.accountLocale !== input.tabLocale
}

/**
 * May an unsent choice be written to THIS account?
 *
 * A choice made on the sign-in page belongs to whoever signs in next — that is the person who
 * made it, seconds earlier, with no session to hold it. A choice made while somebody else was
 * signed in, whose write then failed, does not: on a shared machine it would make one person's
 * language follow another person to every device they own.
 */
export function mayWriteChoiceTo(owner: string, userId: string): boolean {
  return owner === ANONYMOUS || owner === userId
}

export function readUnsentOwner(): string | null {
  try {
    const owner = localStorage.getItem(UNSENT_CHOICE_KEY)
    return owner === null || owner === '' ? null : owner
  } catch {
    // A locked-down profile throws rather than returning null. Nothing survives a reload
    // there, including this; the choice still reaches the account while the tab is open.
    return null
  }
}

export function writeUnsentOwner(owner: string): void {
  try {
    localStorage.setItem(UNSENT_CHOICE_KEY, owner)
  } catch {
    // Best effort — see `readUnsentOwner`.
  }
}

export function clearUnsentOwner(): void {
  try {
    localStorage.removeItem(UNSENT_CHOICE_KEY)
  } catch {
    // Best effort — see `readUnsentOwner`.
  }
}
